import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { Text, Link } from 'components';

interface ConsentTextProps {
  /**
   * Which context to use - determines which labels to use
   * @param context - 'createAccount' uses .label, 'checkout' uses .paymentLabel
   */
  context: 'createAccount' | 'checkout';
}

/**
 * Unified consent text component that handles Terms & Conditions and Privacy Policy links.
 */
const ConsentText: React.FC<ConsentTextProps> = ({ context }) => {
  const { termsAndConditions, privacyPolicy } = useSelector((state: RootState) => ({
    termsAndConditions: state.settings.features?.terms,
    privacyPolicy: state.settings.features?.privacyPolicy,
  }));
  const usePaymentLabels = context === 'checkout';
  
  const termsLabel = usePaymentLabels ? termsAndConditions?.paymentLabel : termsAndConditions?.label;
  const privacyLabel = usePaymentLabels ? privacyPolicy?.paymentLabel : privacyPolicy?.label;

  const isTermsEnabled = !!(termsAndConditions && termsLabel);
  const isPrivacyEnabled = !!(privacyPolicy && privacyLabel);
  const hasAnyContent = isTermsEnabled || isPrivacyEnabled;

  if (!hasAnyContent) {
    return null;
  }

  return (
    <Text fontSize="fine">
      {isTermsEnabled && (
        <>
          {termsLabel}
          {' '}
          <Link
            href={termsAndConditions.url}
            target="_blank"
          >
            {termsAndConditions.urlLabel}
          </Link>
        </>
      )}
      {isTermsEnabled && isPrivacyEnabled && ' '}
      {isPrivacyEnabled && (
        <>
          {privacyLabel}
          {' '}
          <Link
            href={privacyPolicy.url}
            target="_blank"
          >
            {privacyPolicy.urlLabel}
          </Link>
        </>
      )}
      .
    </Text>
  );
};

export default React.memo(ConsentText);
